import { create } from 'zustand';
import type {
  ImportedCurveData,
  SweetSpotEvaluationResult,
  SweetSpotParameterConfig,
} from './types';

const RESULT_STORAGE_PREFIX = 'fracsight:sweetspot-evaluation-result:';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export function isSweetSpotEvaluationResult(value: unknown): value is SweetSpotEvaluationResult {
  if (!isRecord(value)) return false;
  if (typeof value.wellId !== 'string' || !value.wellId) return false;
  if (!isFiniteNumber(value.intervalTop) || !isFiniteNumber(value.intervalBottom)) return false;
  if (value.intervalBottom <= value.intervalTop) return false;
  if (value.depthUnit !== 'm' && value.depthUnit !== 'ft') return false;
  if (!isRecord(value.evaluationConfig)) return false;
  if (typeof value.generatedAt !== 'string') return false;

  if (
    !Array.isArray(value.reservoirQualityCurve) || !Array.isArray(value.completionQualityCurve)
    || !Array.isArray(value.compositeSweetnessCurve) || !Array.isArray(value.highQualityIntervals)
    || !Array.isArray(value.poorQualityIntervals) || !Array.isArray(value.heterogeneityBreakpoints)
    || !Array.isArray(value.recommendedStageBoundaries)
    || !Array.isArray(value.recommendedClusterCandidates) || !Array.isArray(value.sourceCurveIds)
  ) return false;

  const isCurvePoint = (item: unknown) => isRecord(item)
    && isFiniteNumber(item.depth) && isFiniteNumber(item.value);
  const isInterval = (item: unknown) => isRecord(item)
    && isFiniteNumber(item.top) && isFiniteNumber(item.bottom) && isFiniteNumber(item.avgScore);
  const isBreakpoint = (item: unknown) => isRecord(item)
    && isFiniteNumber(item.depth) && typeof item.reason === 'string';
  const isStageBoundary = (item: unknown) => isRecord(item)
    && isFiniteNumber(item.top) && isFiniteNumber(item.bottom) && typeof item.reason === 'string';
  const isClusterCandidate = (item: unknown) => isRecord(item)
    && isFiniteNumber(item.depth) && isFiniteNumber(item.score) && typeof item.reason === 'string';

  return value.reservoirQualityCurve.every(isCurvePoint)
    && value.completionQualityCurve.every(isCurvePoint)
    && value.compositeSweetnessCurve.every(isCurvePoint)
    && value.highQualityIntervals.every(isInterval)
    && value.poorQualityIntervals.every(isInterval)
    && value.heterogeneityBreakpoints.every(isBreakpoint)
    && value.recommendedStageBoundaries.every(isStageBoundary)
    && value.recommendedClusterCandidates.every(isClusterCandidate)
    && value.sourceCurveIds.every((item) => typeof item === 'string');
}

export function loadSweetSpotEvaluationResult(projectId?: number): SweetSpotEvaluationResult | undefined {
  if (!projectId) return undefined;
  const storageKey = `${RESULT_STORAGE_PREFIX}${projectId}`;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (isSweetSpotEvaluationResult(parsed)) return parsed;
    localStorage.removeItem(storageKey);
  } catch {
    try { localStorage.removeItem(storageKey); } catch { /* storage remains unavailable */ }
  }
  return undefined;
}

type SweetSpotWorkspaceState = {
  importedByProject: Record<number, ImportedCurveData | undefined>;
  configByProject: Record<number, SweetSpotParameterConfig | undefined>;
  resultByProject: Record<number, SweetSpotEvaluationResult | undefined>;
  setImportedData: (projectId: number, data?: ImportedCurveData) => void;
  setParameterConfig: (projectId: number, config: SweetSpotParameterConfig) => void;
  setEvaluationResult: (projectId: number, result: SweetSpotEvaluationResult) => boolean;
};

export const useSweetSpotWorkspaceStore = create<SweetSpotWorkspaceState>((set) => ({
  importedByProject: {},
  configByProject: {},
  resultByProject: {},
  setImportedData: (projectId, data) => set((state) => ({
    importedByProject: { ...state.importedByProject, [projectId]: data },
  })),
  setParameterConfig: (projectId, config) => set((state) => ({
    configByProject: { ...state.configByProject, [projectId]: config },
  })),
  setEvaluationResult: (projectId, result) => {
    // 新版本写入 IndexedDB；这里仅更新当前会话，旧 localStorage 单结果键保持只读。
    set((state) => ({
      resultByProject: { ...state.resultByProject, [projectId]: result },
    }));
    return true;
  },
}));
